/** @jsxImportSource @emotion/react */

import { useRef, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import BaseButton from "components/common/Button/Base";
import { PaymentNotice, PaymentNoticeUploadResult } from "models/paymentNotice.model";
import { PALETTE } from "styles/base/variables";
import { uploadPaymentNotice } from "pages/remotes";
import { runWithConcurrency } from "utils/concurrency";

const UPLOAD_BATCH_SIZE = 4;

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];

type FileStatus = "pending" | "uploading" | "success" | "error" | "cancelled";

// 업로드 진행 팝업의 상태 - 불리언 여러 개를 따로 두면 (예: 업로드 중이면서
// 동시에 중단 완료인) 있을 수 없는 조합이 생길 여지가 있어 하나의 상태값으로 둔다.
// idle: 업로드 시작 전(또는 정상 완료 후) · uploading: 전송 중 · cancelling: "중단"을
// 눌러 남은 대기 건이 끝나길 기다리는 중 · cancelled: 중단이 반영되어 "확인"을 기다리는 중
type UploadPhase = "idle" | "uploading" | "cancelling" | "cancelled";

interface FileItem {
  id: string;
  file: File;
  status: FileStatus;
  errorMessage?: string;
}

interface UploadDialogProps {
  isOpen: boolean;
  organizationId: string | null;
  onClose: () => void;
  onUploadFinished: (results: PaymentNoticeUploadResult[]) => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

function UploadDialog({
  isOpen,
  organizationId,
  onClose,
  onUploadFinished,
}: UploadDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>("idle");
  const [completedCount, setCompletedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  // runWithConcurrency의 워커 루프가 즉시 참조할 수 있도록 ref로도 별도 관리한다
  // (uploadPhase state는 클로저에 반영되기까지 지연이 있을 수 있음).
  const cancelRequestedRef = useRef(false);
  // 중단 확정 시점의 최종 결과(성공/실패/미전송 포함)를 "확인" 클릭 때까지 들고 있는다.
  const finalResultsRef = useRef<PaymentNoticeUploadResult[]>([]);

  // uploadPhase에서 파생되는 값들 - 업로드 중/중단 대기 중을 하나로 묶어서 봐야 하는
  // 곳(파일 추가/제거, 드롭존 등)이 많아 편의상 계속 계산해서 쓴다.
  const isUploading = uploadPhase === "uploading" || uploadPhase === "cancelling";
  const isCancelRequested = uploadPhase === "cancelling";
  const isCancelled = uploadPhase === "cancelled";

  const resetState = () => {
    setFileItems([]);
    setIsDragOver(false);
    setUploadPhase("idle");
    setCompletedCount(0);
    setRejectedCount(0);
    cancelRequestedRef.current = false;
    finalResultsRef.current = [];
  };

  const closeDialog = () => {
    if (isUploading || isCancelled) return;
    onClose();
    setTimeout(resetState, 500);
  };

  const addFiles = (files: FileList | File[]) => {
    const accepted: FileItem[] = [];
    let rejected = 0;

    Array.from(files).forEach((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        rejected += 1;
        return;
      }
      accepted.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        status: "pending",
      });
    });

    if (accepted.length > 0) setFileItems((prev) => [...prev, ...accepted]);
    if (rejected > 0) setRejectedCount((prev) => prev + rejected);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading || isCancelled) return;
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeFile = (id: string) => {
    if (isUploading || isCancelled) return;
    setFileItems((prev) => prev.filter((item) => item.id !== id));
  };

  const startUpload = async () => {
    if (!organizationId || fileItems.length === 0 || isUploading) return;

    setUploadPhase("uploading");
    cancelRequestedRef.current = false;
    setCompletedCount(0);

    // 상태 갱신은 화면 표시용이고, 콜백에 넘길 결과는 별도 배열에 모아
    // React state의 배치/타이밍에 영향받지 않도록 한다.
    const results: PaymentNoticeUploadResult[] = [];
    // 중단 시, runWithConcurrency가 실제로 시도한(성공/실패 불문) 건을 가려내
    // 나머지(= 시작조차 못한 대기 건)를 중단됨으로 처리하기 위해 별도로 기록한다.
    const attemptedIds = new Set<string>();

    const uploadOne = async (item: FileItem) => {
      attemptedIds.add(item.id);
      setFileItems((prev) =>
        prev.map((f) =>
          f.id === item.id ? { ...f, status: "uploading" } : f
        )
      );

      try {
        const res = await uploadPaymentNotice(organizationId, item.file);
        const notice: PaymentNotice | null = res?.data || null;
        results.push({ fileName: item.file.name, notice });
        setFileItems((prev) =>
          prev.map((f) => (f.id === item.id ? { ...f, status: "success" } : f))
        );
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || "업로드 중 오류가 발생했습니다.";
        results.push({ fileName: item.file.name, notice: null, errorMessage });
        setFileItems((prev) =>
          prev.map((f) =>
            f.id === item.id ? { ...f, status: "error", errorMessage } : f
          )
        );
      } finally {
        setCompletedCount((prev) => prev + 1);
      }
    };

    // 백엔드 부하를 고려해 동시 업로드는 최대 UPLOAD_BATCH_SIZE건으로 제한한다.
    await runWithConcurrency(
      fileItems,
      UPLOAD_BATCH_SIZE,
      uploadOne,
      () => cancelRequestedRef.current
    );

    // 이미 전송 중이던 건은 끝까지 처리됐고(attemptedIds), 시작도 못한 대기 건만
    // 중단 대상이다. "중단"을 눌렀더라도 그 사이 모든 파일이 이미 처리되어 실제로
    // 중단된 건이 하나도 없다면(흔히 있는 타이밍), 정상 완료로 취급한다 - 그렇지
    // 않으면 아무것도 취소되지 않았는데도 "중단되었습니다" 확인 화면이 떠버린다.
    const cancelledItems = fileItems.filter(
      (item) => !attemptedIds.has(item.id)
    );

    if (cancelRequestedRef.current && cancelledItems.length > 0) {
      cancelledItems.forEach((item) => {
        results.push({
          fileName: item.file.name,
          notice: null,
          errorMessage: "미전송",
          cancelled: true,
        });
      });
      setFileItems((prev) =>
        prev.map((f) =>
          attemptedIds.has(f.id) ? f : { ...f, status: "cancelled" }
        )
      );
      finalResultsRef.current = results;
      setUploadPhase("cancelled");
      return;
    }

    setUploadPhase("idle");
    onUploadFinished(results);
    setTimeout(resetState, 500);
  };

  // 업로드 진행 중 "중단"을 누르면, 이미 시작된 건은 그대로 끝까지 진행하고
  // 아직 시작하지 않은 대기 건만 더 이상 시작하지 않도록 한다.
  const requestCancel = () => {
    if (uploadPhase !== "uploading") return;
    cancelRequestedRef.current = true;
    setUploadPhase("cancelling");
  };

  // 중단이 반영된 뒤 "확인"을 누르면, 그제서야 업로드 흐름을 마무리한다
  // (미전송 건은 결과 화면에서 "미전송"으로 표시됨).
  const confirmCancelled = () => {
    onUploadFinished(finalResultsRef.current);
    setTimeout(resetState, 500);
  };

  const totalCount = fileItems.length;
  const progressPercent =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const renderStatusIcon = (status: FileStatus) => {
    switch (status) {
      case "success":
        return (
          <CheckCircleIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.progressCompleted};
            `}
          />
        );
      case "error":
        return (
          <ErrorIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.error};
            `}
          />
        );
      case "uploading":
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.primary};
            `}
          >
            업로드 중
          </span>
        );
      case "cancelled":
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.error};
            `}
          >
            중단됨
          </span>
        );
      default:
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.textMid};
            `}
          >
            대기
          </span>
        );
    }
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>납부서 파일 업로드</DialogTitle>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div
            onClick={() =>
              !isUploading && !isCancelled && inputRef.current?.click()
            }
            onDragOver={(e) => {
              e.preventDefault();
              if (!isUploading && !isCancelled) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            css={css`
              padding: 32px 16px;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
              border: 2px dashed
                ${isDragOver ? PALETTE.primary : PALETTE.border};
              border-radius: 8px;
              background-color: ${isDragOver
                ? PALETTE.primaryHover
                : PALETTE.background};
              cursor: ${isUploading || isCancelled ? "default" : "pointer"};
              transition: border-color 0.2s, background-color 0.2s;
            `}
          >
            <CloudUploadOutlinedIcon
              css={css`
                color: ${PALETTE.icon};
                font-size: 32px;
              `}
            />
            <div
              css={css`
                font-size: 14px;
                color: ${PALETTE.text};
              `}
            >
              파일을 드래그 앤 드롭하거나 클릭하여 선택하세요
            </div>
            <div
              css={css`
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              이미지(jpg, png, gif, webp) 또는 PDF 파일, 여러 개 선택 가능
            </div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
              onChange={onFileInputChange}
              css={css`
                display: none;
              `}
            />
          </div>

          {rejectedCount > 0 && (
            <div
              css={css`
                font-size: 12px;
                color: ${PALETTE.error};
              `}
            >
              지원하지 않는 형식의 파일 {rejectedCount}개는 제외되었습니다.
            </div>
          )}

          {fileItems.length > 0 && (
            <div
              css={css`
                max-height: 280px;
                overflow-y: auto;
                border: 1px solid ${PALETTE.border};
                border-radius: 8px;
              `}
            >
              {fileItems.map((item) => (
                <div
                  key={item.id}
                  css={css`
                    padding: 10px 14px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    border-bottom: 1px solid ${PALETTE.borderLight};

                    &:last-child {
                      border-bottom: none;
                    }
                  `}
                >
                  <InsertDriveFileOutlinedIcon
                    fontSize="small"
                    css={css`
                      color: ${PALETTE.icon};
                    `}
                  />
                  <div
                    css={css`
                      flex: 1;
                      min-width: 0;
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <div
                      css={css`
                        font-size: 13px;
                        color: ${PALETTE.text};
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      `}
                    >
                      {item.file.name}
                    </div>
                    <div
                      css={css`
                        font-size: 11px;
                        color: ${PALETTE.textMid};
                      `}
                    >
                      {formatFileSize(item.file.size)}
                      {item.status === "error" &&
                        item.errorMessage &&
                        ` · ${item.errorMessage}`}
                    </div>
                  </div>
                  {renderStatusIcon(item.status)}
                  {item.status === "pending" && (
                    <IconButton
                      size="small"
                      onClick={() => removeFile(item.id)}
                    >
                      <CloseIcon
                        fontSize="small"
                        css={css`
                          color: ${PALETTE.icon};
                        `}
                      />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
          )}

          {isCancelled && (
            <div
              css={css`
                font-size: 12px;
                color: ${PALETTE.error};
              `}
            >
              중단되었습니다. 전송되지 않은 파일은 결과 화면에 "미전송"으로
              표시됩니다.
            </div>
          )}

          {(isUploading || isCancelled) && (
            <div>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  backgroundColor: PALETTE.progressBackground,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: PALETTE.progressCompleted,
                  },
                }}
              />
              <div
                css={css`
                  margin-top: 4px;
                  text-align: right;
                  font-size: 12px;
                  color: ${PALETTE.textMid};
                `}
              >
                {completedCount}/{totalCount}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
        `}
      >
        {isCancelled ? (
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={confirmCancelled}
          >
            확인
          </BaseButton>
        ) : isUploading ? (
          <BaseButton
            variant="outlined"
            color={PALETTE.error}
            disabled={isCancelRequested}
            onClick={requestCancel}
          >
            {isCancelRequested ? "중단 중" : "중단"}
          </BaseButton>
        ) : (
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeDialog}
          >
            닫기
          </BaseButton>
        )}
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          disabled={fileItems.length === 0 || isUploading || isCancelled}
          onClick={startUpload}
        >
          {isUploading ? "업로드 중" : "업로드"}
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadDialog;
